import React, { useState, useEffect, useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { PageLayout, PageHeader } from '../../components'
import { authenticatedFetch } from '../../services/api/apiClient'
import { apiEndpoints } from '../../consts'
import usePendingSetupCount from '../../hooks/usePendingSetupCount'
import useBillingDraftCount from '../../hooks/useBillingDraftCount'
import useBillingDashboardData from '../../hooks/useBillingDashboardData'
import AdminBillingDashboard from './AdminBillingDashboard'
import AdminBillingProfiles from './AdminBillingProfiles'
import AdminBillingUnlinkedAccounts from './AdminBillingUnlinkedAccounts'
import AdminBillingPendingReview from './AdminBillingPendingReview'
import styles from './AdminBillingManagement.module.scss'

const TABS = [
  { id: 'dashboard', label: 'Dashboard', icon: 'money-bill-wave' },
  { id: 'pending', label: 'Invoice Management', icon: 'clipboard-check' },
  { id: 'profiles', label: 'Client Profiles', icon: 'file-invoice-dollar' },
  { id: 'unlinked', label: 'Unlinked Setup', icon: 'unlink' },
]

const TAB_COMPONENTS = {
  dashboard: AdminBillingDashboard,
  pending: AdminBillingPendingReview,
  profiles: AdminBillingProfiles,
  unlinked: AdminBillingUnlinkedAccounts,
}

const AdminBillingManagement = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const pendingSetup = usePendingSetupCount(true)
  const draftCount = useBillingDraftCount(true)
  const { stats: dashboardStats } = useBillingDashboardData()
  const isNonProd = !!dashboardStats?.appEnv && dashboardStats.appEnv !== 'production'

  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(location.search)
    const tab = params.get('tab')
    return TABS.some((t) => t.id === tab) ? tab : 'dashboard'
  })

  /* eslint-disable react-hooks/set-state-in-effect -- sync tab state from URL on back/forward navigation */
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const tab = params.get('tab')
    if (tab && TABS.some((t) => t.id === tab) && tab !== activeTab) {
      setActiveTab(tab)
    }
  }, [location.search, activeTab])
  /* eslint-enable react-hooks/set-state-in-effect */

  const handleTabChange = (tabId) => {
    setActiveTab(tabId)
    navigate(`/billing/admin?tab=${tabId}`, { replace: true })
  }

  const [exportOpen, setExportOpen] = useState(false)
  const [exportPassword, setExportPassword] = useState('')
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState(null)

  const [clearOpen, setClearOpen] = useState(false)
  const [clearing, setClearing] = useState(false)
  const [clearError, setClearError] = useState(null)
  const [clearResult, setClearResult] = useState(null)

  const handleExport = useCallback(async () => {
    setExporting(true)
    setExportError(null)
    try {
      const res = await authenticatedFetch(apiEndpoints.billingExportBankDetails, {
        method: 'POST',
        body: JSON.stringify({ password: exportPassword }),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.message || `Export failed (${res.status})`)
      }
      const blob = await res.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `bank-details-export-${new Date().toISOString().split('T')[0]}.csv`
      a.click()
      window.URL.revokeObjectURL(url)
      setExportOpen(false)
      setExportPassword('')
    } catch (err) {
      setExportError(err.message)
    } finally {
      setExporting(false)
    }
  }, [exportPassword])

  const handleClearPending = useCallback(async () => {
    setClearing(true)
    setClearError(null)
    try {
      const res = await authenticatedFetch(apiEndpoints.billingClearPendingInvoices, { method: 'POST' })
      const json = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(json.message || `Clear failed (${res.status})`)
      setClearResult(json.data || { cleared: 0, stripeVoided: 0, stripeFailed: 0 })
    } catch (err) {
      setClearError(err.message)
    } finally {
      setClearing(false)
    }
  }, [])

  const ActiveComponent = TAB_COMPONENTS[activeTab]

  return (
    <PageLayout>
      <div className={styles.headerRow}>
        <PageHeader
          title="Billing Management"
          subtitle="Invoicing, client profiles, and account setup"
        />
        <button
          type="button"
          className={styles.exportBtn}
          onClick={() => {
            setExportOpen(true)
            setExportError(null)
            setExportPassword('')
          }}
        >
          <FontAwesomeIcon icon="download" /> Export Bank Details
        </button>
        {isNonProd && (
          <button
            type="button"
            className={styles.exportBtn}
            style={{ marginLeft: 8 }}
            onClick={() => {
              setClearOpen(true)
              setClearError(null)
              setClearResult(null)
            }}
          >
            <FontAwesomeIcon icon="trash" /> Clear Pending Invoices
          </button>
        )}
      </div>

      <Dialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          style: { backgroundColor: 'var(--color-card-bg)', color: 'var(--color-text-primary)' },
        }}
      >
        <DialogTitle>Export Bank Details</DialogTitle>
        <DialogContent>
          <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', margin: '0 0 16px' }}>
            This will download all PAD/ACH bank details in decrypted CSV format. Enter the export
            password to continue.
          </p>
          <TextField
            autoFocus
            fullWidth
            type="password"
            label="Export Password"
            value={exportPassword}
            onChange={(e) => setExportPassword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && exportPassword) handleExport()
            }}
            error={!!exportError}
            helperText={exportError || ''}
            size="small"
          />
        </DialogContent>
        <DialogActions style={{ padding: '8px 24px 16px' }}>
          <Button onClick={() => setExportOpen(false)} disabled={exporting}>
            Cancel
          </Button>
          <Button
            onClick={handleExport}
            variant="contained"
            disabled={!exportPassword || exporting}
            style={{
              backgroundColor:
                exportPassword && !exporting ? 'var(--color-brand-primary)' : undefined,
            }}
          >
            {exporting ? 'Exporting...' : 'Download CSV'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={clearOpen}
        onClose={() => { if (!clearing) setClearOpen(false) }}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          style: { backgroundColor: 'var(--color-card-bg)', color: 'var(--color-text-primary)' },
        }}
      >
        <DialogTitle>Clear Pending Invoices</DialogTitle>
        <DialogContent>
          {clearResult ? (
            <p style={{ fontSize: 13, color: 'var(--color-text-primary)', margin: 0 }}>
              Cleared {clearResult.cleared} draft/pending invoice(s). Stripe: {clearResult.stripeVoided} voided, {clearResult.stripeFailed} failed.
            </p>
          ) : (
            <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', margin: 0 }}>
              Non-production only. This permanently deletes ALL draft and pending invoices locally and
              voids/deletes their Stripe drafts. This cannot be undone.
            </p>
          )}
          {clearError && (
            <p style={{ fontSize: 13, color: 'var(--color-error)', marginTop: 12 }}>{clearError}</p>
          )}
        </DialogContent>
        <DialogActions style={{ padding: '8px 24px 16px' }}>
          {clearResult ? (
            <Button onClick={() => window.location.reload()} variant="contained">
              Done
            </Button>
          ) : (
            <>
              <Button onClick={() => setClearOpen(false)} disabled={clearing}>
                Cancel
              </Button>
              <Button
                onClick={handleClearPending}
                variant="contained"
                disabled={clearing}
                style={{ backgroundColor: 'var(--color-error)' }}
              >
                {clearing ? 'Clearing...' : 'Clear All'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <div className={styles.tabBar}>
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`${styles.tab} ${activeTab === tab.id ? styles.tabActive : ''}`}
            onClick={() => handleTabChange(tab.id)}
          >
            <FontAwesomeIcon icon={tab.icon} className={styles.tabIcon} />
            {tab.label}
            {(tab.id === 'profiles' || tab.id === 'unlinked') && pendingSetup.count > 0 && (
              <span className={styles.tabBadge}>{pendingSetup.count}</span>
            )}
            {tab.id === 'pending' && draftCount > 0 && (
              <span className={styles.tabBadge}>{draftCount}</span>
            )}
          </button>
        ))}
      </div>

      <div className={styles.tabContent}>
        <ActiveComponent embedded />
      </div>
    </PageLayout>
  )
}

export default AdminBillingManagement
